import { isEmpty } from 'lodash';
import { replace } from 'redux-first-router';

import { querySel } from '@location';
import {
  ROUTE_BULK_BLUE_CARD,
  ROUTE_BULK_ENTRY_ADVENTURE,
  ROUTE_BULK_ENTRY_AWARD,
  ROUTE_BULK_ENTRY_MB,
} from '@modules/bulkEntry/duck/actions';
import {
  ROUTE_QE_CALENDAR,
  advancementTabNames,
  emptyObj,
  navigateToRosterTab,
  organizationPositionsSel,
  setOrganization,
} from '@shared';
import { lazier } from '@shared/components';
import {
  ROUTE_ADVANCEMENT,
  ROUTE_PURCHASE_ORDER_DETAILS,
  ROUTE_QE_HEALTH,
  ROUTE_QE_OA,
  ROUTE_QE_RANK,
  ROUTE_QE_SCHOOL,
  ROUTE_QE_SCOUTLEADERSHIP,
  ROUTE_QE_SWIMMING,
} from '@shared/duck/actions';
import { newPermissions } from '@shared/newPermissions';

import { AdvancementPage, AdvancementPagePlaceholder } from './components';
import { setActiveAdvancementsTab } from './pendingItems';
import { AdvancementsTabsNames } from './pendingItems/constants';

const routes = {
  [ROUTE_ADVANCEMENT]: {
    path: '/roster',
    Component: AdvancementPage,
    Placeholder: AdvancementPagePlaceholder,
    titleId: 'page.roster',
    thunk: (dispatch, getState) => {
      const state = getState();
      const positions = organizationPositionsSel(state);
      const { organizationGuid = '', advancementTab = '' } =
        querySel(state) || emptyObj;
      const youthOrgGuid = organizationGuid;

      if (!isEmpty(youthOrgGuid) && !isEmpty(positions)) {
        const foundPosition = positions.find(
          p => p.organizationGuid === youthOrgGuid,
        );

        // SBL-4463 : Added due to hyperlinks coming from Scoutbook
        // HyperLink : /roster?organizationGuid=:orgGuid&advancementTab=toPurchase
        if (advancementTab === AdvancementsTabsNames.TO_PURCHASE) {
          dispatch(setActiveAdvancementsTab(AdvancementsTabsNames.TO_PURCHASE));
          dispatch(navigateToRosterTab(advancementTabNames.ADVANCEMENTS));
        } else if (advancementTab === AdvancementsTabsNames.TO_APPROVE) {
          // SB-3876 : Scoutbook story
          dispatch(setActiveAdvancementsTab(AdvancementsTabsNames.TO_APPROVE));
          dispatch(navigateToRosterTab(advancementTabNames.ADVANCEMENTS));
        } else if (advancementTab === AdvancementsTabsNames.TO_AWARD) {
          dispatch(setActiveAdvancementsTab(AdvancementsTabsNames.TO_AWARD));
          dispatch(navigateToRosterTab(advancementTabNames.ADVANCEMENTS));
        } else {
          replace('/roster');
        }

        if (foundPosition) {
          dispatch(
            setOrganization({
              guid: foundPosition.organizationGuid,
              programType: foundPosition.programType,
              parentOrgGuid: foundPosition.organizationGuid,
            }),
          );
        }
      }
    },
    permission: newPermissions.VIEW_ROSTER_PAGE,
  },
  [ROUTE_QE_HEALTH]: {
    path: '/roster/health',
    Component: lazier(
      () => import('./quickEntryHealth/QEHealthPage'),
      AdvancementPagePlaceholder,
    ),
    Placeholder: AdvancementPagePlaceholder,
    titleId: 'page.health',
    permission: newPermissions.VIEW_ROSTER_PAGE,
  },
  [ROUTE_QE_RANK]: {
    path: '/roster/bulk-entry-rank/:unitGuid',
    Component: lazier(
      () => import('../bulkEntry/components/BulkEntryRankPage'),
      AdvancementPagePlaceholder,
    ),
    Placeholder: AdvancementPagePlaceholder,
    titleId: 'page.bulk-entry.rank',
    permission: newPermissions.VIEW_ROSTER_PAGE,
  },
  [ROUTE_BULK_ENTRY_MB.type]: {
    path: '/roster/bulk-entry-mb/:unitGuid',
    Component: lazier(
      () => import('../bulkEntry/components/BulkMeritBadge/BulkMeritBadgePage'),
      AdvancementPagePlaceholder,
    ),
    Placeholder: AdvancementPagePlaceholder,
    titleId: 'page.bulk-entry.meritBadge',
    permission: newPermissions.EDIT_ADVANCEMENTS,
  },
  [ROUTE_BULK_ENTRY_AWARD.type]: {
    path: '/roster/bulk-entry-award/:unitGuid',
    Component: lazier(
      () =>
        import('../bulkEntry/components/BulkEntryAwards/BulkEntryAwardsPage'),
      AdvancementPagePlaceholder,
    ),
    Placeholder: AdvancementPagePlaceholder,
    titleId: 'page.bulk-entry.award',
    permission: [
      newPermissions.EDIT_ADVANCEMENTS,
      newPermissions.EDIT_SUB_ADVANCEMENTS,
    ],
  },
  [ROUTE_BULK_ENTRY_ADVENTURE.type]: {
    path: '/roster/bulk-entry-adventure/:unitGuid',
    Component: lazier(
      () => import('../bulkEntry/components/BulkAdventures/BulkAdventuresPage'),
      AdvancementPagePlaceholder,
    ),
    Placeholder: AdvancementPagePlaceholder,
    titleId: 'page.bulk-entry.adventure',
    permission: [
      newPermissions.EDIT_ADVANCEMENTS,
      newPermissions.EDIT_SUB_ADVANCEMENTS,
    ],
  },
  [ROUTE_BULK_BLUE_CARD.type]: {
    path: '/roster/bulk-blue-card/:unitGuid',
    Component: lazier(
      () =>
        import(
          '../bulkEntry/components/BulkMeritBadgeBlueCard/BulkBlueCardPage'
        ),
      AdvancementPagePlaceholder,
    ),
    Placeholder: AdvancementPagePlaceholder,
    titleId: 'page.bulk-entry.blueCard',
    permission: newPermissions.EDIT_ADVANCEMENTS,
  },
  [ROUTE_QE_SWIMMING]: {
    path: '/roster/swimming',
    Component: lazier(
      () => import('./quickEntrySwimming/QESwimmingPage'),
      AdvancementPagePlaceholder,
    ),
    Placeholder: AdvancementPagePlaceholder,
    titleId: 'page.swimming',
    permission: newPermissions.VIEW_ROSTER_PAGE,
  },
  [ROUTE_QE_SCHOOL]: {
    path: '/roster/school',
    Component: lazier(
      () => import('./quickEntrySchool/QESchoolPage'),
      AdvancementPagePlaceholder,
    ),
    Placeholder: AdvancementPagePlaceholder,
    titleId: 'page.school',
    permission: newPermissions.VIEW_ROSTER_PAGE,
  },
  [ROUTE_QE_SCOUTLEADERSHIP]: {
    path: '/roster/leadership',
    Component: lazier(
      () => import('./quickEntryLeadership/QELeadershipPage'),
      AdvancementPagePlaceholder,
    ),
    Placeholder: AdvancementPagePlaceholder,
    titleId: 'page.leadership',
    permission: newPermissions.VIEW_ROSTER_PAGE,
  },
  [ROUTE_QE_OA]: {
    path: '/roster/oa',
    Component: lazier(
      () => import('./quickEntryOA/QEOAPage'),
      AdvancementPagePlaceholder,
    ),
    Placeholder: AdvancementPagePlaceholder,
    titleId: 'page.oa',
    permission: newPermissions.VIEW_ROSTER_PAGE,
  },
  [ROUTE_QE_CALENDAR]: {
    path: '/roster/calendar',
    Component: lazier(
      () => import('./quickEntryCalendar/QECalendarPage'),
      AdvancementPagePlaceholder,
    ),
    Placeholder: AdvancementPagePlaceholder,
    titleId: 'page.quick-entry.calendar',
    permission: newPermissions.VIEW_ROSTER_PAGE,
  },
  [ROUTE_PURCHASE_ORDER_DETAILS]: {
    path: '/roster/purchaseOrderDetails',
    Component: lazier(
      () => import('./purchaseOrder/components/PODetailsPage/PODetailsPage'),
      AdvancementPagePlaceholder,
    ),
    Placeholder: AdvancementPagePlaceholder,
    titleId: 'page.purchaseOrderDetails',
    permission: newPermissions.VIEW_ROSTER_PAGE,
  },
};

export default routes;
